import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MapPin, Building2, Store, HeartHandshake } from "lucide-react";
import { Reveal } from "./Reveal";
import { scrollToSection } from "./scroll";

const IMG_BUILDING = "/photos/adams-way-rendering.jpg";
const IMG_CARE = "/photos/meg.jpg";

const FACTS = [
  { icon: Building2, label: "41 inclusive homes above street level" },
  { icon: Store, label: "A public marketplace & community space below" },
  { icon: MapPin, label: "180–190 S Clinton Street, Doylestown Borough" },
  { icon: HeartHandshake, label: "Named for Adam Sklar, a neighbor who inspired it all" },
];

const MILESTONES = [
  { date: "Feb 2026", label: "Site acquired in Doylestown Borough" },
  { date: "2026", label: "Concept unveiled, zoning approvals secured" },
  { date: "Next", label: "Final design & permitting, then ~2 years of construction" },
];

const GOAL = 5_000_000;
const RAISED = 1_050_000;

const AdamsWay = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [-40, 60]);

  const pct = Math.round((RAISED / GOAL) * 100);

  return (
    <section
      id="adams-way"
      ref={ref}
      data-testid="adams-way-section"
      className="py-28 lg:py-40 bg-secondary/30"
    >
      <div className="max-w-[90rem] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-12 gap-8 lg:gap-14 items-start">
          <div className="col-span-12 lg:col-span-7 order-2 lg:order-1">
            <Reveal>
              <div className="group relative aspect-[16/10] max-w-xl rounded-3xl overflow-hidden border border-foreground/10" data-testid="adams-way-image">
                <motion.img
                  src={IMG_BUILDING}
                  alt="Official concept rendering of Adam's Way on South Clinton Street, Doylestown"
                  loading="lazy"
                  style={{ y: imgY, scale: 1.12 }}
                  className="w-full h-full object-cover will-change-transform transition-[scale] duration-700"
                />
              </div>
              <p className="mt-4 text-xs uppercase tracking-[0.15em] text-muted-foreground">
                Concept rendering · Union Studio Architecture &amp; Community Design
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 grid grid-cols-12 gap-6 items-end">
                <div className="col-span-5">
                  <div className="aspect-square overflow-hidden rounded-2xl ring-1 ring-foreground/10">
                    <img
                      src={IMG_CARE}
                      alt="A smiling member of The Next Step Programs community"
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    />
                  </div>
                </div>
                <div className="col-span-7">
                  <p className="font-serif italic font-light text-xl lg:text-2xl text-foreground/80 leading-snug max-w-sm">
                    "A community where adults of all abilities
                    live, work, and belong, together."
                  </p>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="col-span-12 lg:col-span-5 order-1 lg:order-2 lg:sticky lg:top-32">
            <Reveal>
              <p className="overline-label mb-6" data-testid="adams-way-overline">
                Our Flagship Project
              </p>
              <h2
                className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight"
                data-testid="adams-way-heading"
              >
                Adam's <em className="text-primary font-normal">Way</em>
              </h2>
              <p className="mt-6 text-base lg:text-lg leading-relaxed text-muted-foreground max-w-lg">
                A first-of-its-kind, mixed-use community in the heart of
                Doylestown Borough, where adults with and without
                disabilities share a building, a marketplace, and a neighborhood.
                Roughly 41 homes rise above a street-level gathering space
                open to the whole town.
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              <ul className="mt-10 space-y-5">
                {FACTS.map((f, i) => (
                  <li key={i} className="flex items-start gap-4 group" data-testid={`adams-way-fact-${i}`}>
                    <span className="mt-0.5 text-primary shrink-0 transition-transform duration-300 group-hover:scale-110">
                      <f.icon size={19} />
                    </span>
                    <span className="text-base text-foreground/85">{f.label}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-12 border border-foreground/10 rounded-2xl bg-card p-7 transition-colors duration-500 hover:border-primary/30" data-testid="campaign-progress">
                <div className="flex items-end justify-between mb-3">
                  <p className="font-serif text-3xl lg:text-4xl text-foreground">
                    $1.05M <span className="text-lg text-muted-foreground">raised</span>
                  </p>
                  <p className="text-sm text-muted-foreground">of $5M community campaign</p>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${pct}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
                    className="h-full rounded-full bg-primary"
                    data-testid="campaign-progress-bar"
                  />
                </div>
                <p className="mt-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">
                  {pct}% of a $25M vision made possible by neighbors
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="mt-10 space-y-4">
                {MILESTONES.map((m, i) => (
                  <div key={i} className="flex items-baseline gap-5" data-testid={`adams-way-milestone-${i}`}>
                    <span className="font-serif italic text-primary text-lg w-20 shrink-0">
                      {m.date}
                    </span>
                    <span className="text-sm text-muted-foreground border-l border-foreground/15 pl-5">
                      {m.label}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-6">
                <a
                  href="#interest"
                  data-testid="adams-way-interest-link"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("#interest");
                  }}
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground transition-[background-color,transform] duration-300 hover:bg-lavender-deep hover:-translate-y-0.5"
                >
                  Join the interest list
                </a>
                <a
                  href="https://adamswaypa.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="adams-way-external-link"
                  className="inline-flex items-center gap-2 link-line text-sm font-medium text-foreground"
                >
                  Visit the Adam's Way project site
                  <ArrowUpRight size={16} className="text-primary" />
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdamsWay;
